/**
 * RemoteSelect — 汎用のリモート・データソースから取ってくるドロップダウン。
 * LookupCombobox が対応しない任意のマスタ（仕入先・受注分類・棚など）向け。
 *
 * Props:
 *   fetch      : async () => [{id, name, ...}] — マスタ取得関数
 *   value      : 選択中のID（またはnull）
 *   onChange   : (item|null) => void — 選択された行オブジェクトを返す
 *   labelKey   : 表示用フィールド（既定: "name"）
 *   secondaryKey : サブ表示（例: code / location）
 *   placeholder / disabled / testId / className
 *   allowClear : true でクリアボタンを表示
 *   emptyHint  : 空の時に出す案内（例: 「棚マスタから追加してください」）
 *   invalid    : true でエラー枠
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { CaretDown, MagnifyingGlass, Check, X } from '@phosphor-icons/react';
import { Input } from './input';

export function RemoteSelect({
  fetch, value, onChange, labelKey = 'name', secondaryKey,
  placeholder = '選択…', disabled = false, testId,
  className = '', inputClassName = '',
  allowClear = false, emptyHint = 'データがありません', invalid = false,
}) {
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch().then((data) => {
      if (!cancelled) setOptions(Array.isArray(data) ? data : []);
    }).catch(() => { if (!cancelled) setOptions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fetch]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = useMemo(() => options.find((o) => o.id === value) || null, [options, value]);
  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) =>
      String(o[labelKey] || '').toLowerCase().includes(q)
      || String(o[secondaryKey] || '').toLowerCase().includes(q)
    );
  }, [options, query, labelKey, secondaryKey]);

  const pick = (o) => { onChange?.(o); setOpen(false); setQuery(''); };
  const clear = (e) => { e.stopPropagation(); onChange?.(null); };

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testId}>
      <button type="button" disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`h-10 w-full flex items-center gap-1.5 px-3 rounded-md border bg-white text-left text-sm transition-colors ${
          disabled ? 'opacity-60 cursor-not-allowed' : 'hover:border-zinc-400'
        } ${invalid ? 'border-rose-400' : 'border-zinc-200'} ${inputClassName}`}
        data-testid={testId ? `${testId}-trigger` : undefined}>
        <span className={`flex-1 truncate ${selected ? 'text-zinc-800' : 'text-zinc-400'}`}>
          {selected ? selected[labelKey] : placeholder}
        </span>
        {allowClear && selected && !disabled && (
          <span onClick={clear} className="p-0.5 rounded hover:bg-zinc-100 text-zinc-400 hover:text-rose-500"
            data-testid={testId ? `${testId}-clear` : undefined} role="button" tabIndex={-1}>
            <X size={12} weight="bold" />
          </span>
        )}
        <CaretDown size={12} weight="bold" className="text-zinc-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-zinc-200 rounded-md shadow-lg overflow-hidden">
          <div className="relative border-b border-zinc-100">
            <MagnifyingGlass size={12} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="検索…" autoFocus className="h-8 pl-7 text-xs border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
              data-testid={testId ? `${testId}-search` : undefined} />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {loading && <div className="px-3 py-3 text-xs text-zinc-400">読込中…</div>}
            {!loading && filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-zinc-500">{query ? '該当なし' : emptyHint}</div>
            )}
            {!loading && filtered.map((o) => (
              <button key={o.id} type="button" onClick={() => pick(o)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-50"
                data-testid={testId ? `${testId}-opt-${o.id}` : undefined}>
                <Check size={12} weight="bold" className={o.id === value ? 'text-emerald-600' : 'invisible'} />
                <span className="flex-1 truncate">{o[labelKey]}</span>
                {secondaryKey && o[secondaryKey] && (
                  <span className="text-[10px] font-mono text-zinc-400">{o[secondaryKey]}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default RemoteSelect;
